import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import {
  fetchUserLabels,
  loadManagedDomains,
  loadMemberAnnotations,
} from "services/fde/tciManagerService";
import { listTciRegistry } from "services/fde/fdeRegistryService";

const TciManagerMemberPage = () => {
  const { memberUid } = useParams();
  const { user, isGlobalAdmin, userData, organizationId } = useAuth();
  const { isFdeDc, loading: fdeLoading } = useFdeRole();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;

  const [memberName, setMemberName] = useState("");
  const [annotations, setAnnotations] = useState([]);
  const [tciNameById, setTciNameById] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const canAccess = isFdeDc || isGlobalAdmin;

  const load = useCallback(async () => {
    if (!memberUid || !institutionId || !user?.uid || !canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      const domains = await loadManagedDomains({ uid: user.uid, institutionId });
      const managedTciIds = [
        ...new Set(domains.flatMap((domain) => domain.tciIds || []).filter(Boolean)),
      ];

      const [rows, labels, tcis] = await Promise.all([
        loadMemberAnnotations({ institutionId, teacherUid: memberUid }),
        fetchUserLabels([memberUid]),
        listTciRegistry({ institutionId }),
      ]);

      const filtered = rows.filter((row) => {
        if (row.tciId && managedTciIds.includes(row.tciId)) return true;
        return false;
      });

      setMemberName(labels[memberUid] || memberUid);
      setAnnotations(filtered);
      setTciNameById(Object.fromEntries(tcis.map((row) => [row.id, row.name || row.id])));
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load annotation history.");
    } finally {
      setLoading(false);
    }
  }, [memberUid, institutionId, user?.uid, canAccess]);

  useEffect(() => {
    if (!fdeLoading) load();
  }, [load, fdeLoading]);

  if (fdeLoading || loading) return <Loading page />;

  if (!canAccess) {
    return <Alert severity="warning">TCI Manager access required.</Alert>;
  }

  return (
    <Box>
      <DashboardPageHeader
        title={`${memberName}'s annotations`}
        subtitle="Annotation history for clinicians on your team."
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>TCI</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Observation</TableCell>
              <TableCell>Session</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {annotations.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  {row.createdAtDate ? format(row.createdAtDate, "MMM d, yyyy") : "—"}
                </TableCell>
                <TableCell>{tciNameById[row.tciId] || row.tciId || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.sessionAnnotation?.severity || "medium"}
                    color={row.sessionAnnotation?.severity === "critical" ? "error" : "default"}
                  />
                </TableCell>
                <TableCell>{row.status || "—"}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" noWrap>
                    {row.sessionAnnotation?.notes || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.sessionId ? (
                    <Typography
                      component={RouterLink}
                      to={`/dashboard/sessions/${row.sessionId}`}
                      variant="body2"
                      color="primary"
                    >
                      View session
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!annotations.length && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" variant="body2">
                    No annotations from this clinician in your TCIs yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Typography component={RouterLink} to="/dashboard/tci-manager" variant="body2" color="primary">
          Back to dashboard
        </Typography>
      </Stack>
    </Box>
  );
};

export default TciManagerMemberPage;
